import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { apiService } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import StatusPill from '../../flow/StatusPill';
import { StatusRow } from './StagesOwners';
import * as fmt from '../../../lib/format';

interface Unmapped { alias: string; example: string; uses: number; last_seen: string }

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';

function UnmappedRow({ item, statuses, onMapped }: { item: Unmapped; statuses: StatusRow[]; onMapped: () => void }) {
  const { showToast } = useToast();
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const map = async () => {
    if (!target) return;
    setSaving(true);
    try {
      const r = await apiService.addFlowAlias(item.alias, target);
      if (r.success) { showToast('success', `"${item.example}" now counts as ${target}`); onMapped(); }
      else showToast('error', r.error || 'Could not map the name');
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Could not map the name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5">
      <div className="min-w-0 basis-56">
        <div className="truncate text-sm font-medium text-gray-900">{item.example}</div>
        <div className="text-xs tabular-nums text-gray-500">
          seen {fmt.count(item.uses)} {item.uses === 1 ? 'time' : 'times'} · last {fmt.date(item.last_seen)}
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-500">
        <span>Counts as</span>
        <select value={target} onChange={(e) => setTarget(e.target.value)} className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100">
          <option value="">Choose a status</option>
          {statuses.map((s) => <option key={s.status} value={s.status}>{s.status}</option>)}
        </select>
      </label>
      <button type="button" disabled={!target || saving} onClick={() => void map()} className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-[background-color,transform] duration-150 hover:bg-blue-700 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2">
        Map
      </button>
    </li>
  );
}

function AliasChips({ row, onChanged }: { row: StatusRow; onChanged: () => void }) {
  const { showToast } = useToast();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const alias = name.trim();
    if (!alias) return;
    setBusy(true);
    try {
      const r = await apiService.addFlowAlias(alias, row.status);
      if (r.success) { setName(''); onChanged(); }
      else showToast('error', r.error || 'Could not add the name');
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Could not add the name');
    } finally {
      setBusy(false);
    }
  };
  const remove = async (alias: string) => {
    setBusy(true);
    try {
      const r = await apiService.removeFlowAlias(alias);
      if (r.success) onChanged();
      else showToast('error', r.error || 'Could not remove the name');
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Could not remove the name');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="grid gap-x-4 gap-y-2 px-5 py-3.5 lg:grid-cols-[13rem_minmax(0,1fr)] lg:items-center">
      <div><StatusPill status={row.status} /></div>
      <div className="flex flex-wrap items-center gap-1.5">
        {row.aliases.map((a) => (
          <span key={a} className="inline-flex items-center gap-1 rounded-full bg-gray-100 py-0 pl-2.5 pr-0.5 text-xs text-gray-700">
            {a}
            <button type="button" aria-label={`Stop counting "${a}" as ${row.status}`} disabled={busy} onClick={() => void remove(a)} className="rounded-full p-1.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900 active:scale-[0.92] disabled:opacity-50 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); void add(); }} className="flex items-center gap-1.5">
          <label>
            <span className="sr-only">Add a name that counts as {row.status}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a name" className="w-36 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-900 placeholder:text-gray-500 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          </label>
          <button type="submit" disabled={busy || !name.trim()} className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-300 transition-[background-color,transform] duration-150 hover:bg-blue-50 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
            Add
          </button>
        </form>
      </div>
    </li>
  );
}

// Jira status names drift (IN QA, On Staging, QA Passed). This folds the variants onto one status so
// they are judged the same way, and lists any new name that has not been mapped yet.
export default function StatusNames() {
  const [statuses, setStatuses] = useState<StatusRow[]>([]);
  const [unmapped, setUnmapped] = useState<Unmapped[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiService.getFlowStatuses();
      if (r.success) {
        setStatuses(r.data.statuses ?? []);
        setUnmapped(r.data.unmapped ?? []);
        setError(null);
      } else setError(r.error || 'Could not load status names');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load status names');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-5">
      <section className={CARD} aria-label="Unmapped status names">
        <div className="border-b border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-900">Names not mapped yet</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            Status names seen in the last 12 months that do not count as any status above. Moves into them are not judged until you map them.
          </p>
        </div>
        {error && <div className="m-5 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">{error}</div>}
        {loading ? (
          <div className="h-16 animate-pulse bg-gray-50/60 motion-reduce:animate-none" role="status" aria-label="Loading status names" />
        ) : unmapped.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-500">Every status name used in the last 12 months is mapped.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {unmapped.map((u) => <UnmappedRow key={u.alias} item={u} statuses={statuses} onMapped={() => void load()} />)}
          </ul>
        )}
      </section>

      <section className={CARD} aria-label="Names counted as each status">
        <div className="border-b border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-900">Names counted as each status</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">Spelling and case do not matter. The status name itself always counts, so only the other names are listed.</p>
        </div>
        {!loading && (
          <ul className="divide-y divide-gray-100">
            {statuses.map((s) => <AliasChips key={s.status} row={s} onChanged={() => void load()} />)}
          </ul>
        )}
      </section>
    </div>
  );
}
